import { getRouteApi } from '@tanstack/react-router';
import bbox from '@turf/bbox';
import type { MapGeoJSONFeature, MapRef } from '@vis.gl/react-maplibre';
import Map, { AttributionControl } from '@vis.gl/react-maplibre';
import type { Geometry } from 'geojson';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import 'maplibre-gl/dist/maplibre-gl.css';
import { BorderShape } from '@/components/map/BorderShape';
import { ContextMenuMarker } from '@/components/map/ContextMenuMarker';
import { FeatureSelectMenuMarker } from '@/components/map/FeatureSelectMenuMarker';
import { JsonLayer } from '@/components/map/JsonLayer';
import { LayerVisible } from '@/components/map/LayerVisible';
import { LevelOfServiceLayer } from '@/components/map/LevelOfServiceLayer';
import { ParkingLayer } from '@/components/map/ParkingLayer';
import { RouteLayers } from '@/components/map/RouteLayers';
import { RouteLocationMarkers } from '@/components/map/RouteLocationMarkers';
import { TrafficReportLayer } from '@/components/map/TrafficReportLayer';
import { LayerPopup } from '@/components/popup/LayerPopup';
import { ParkingPopup } from '@/components/popup/ParkingPopup';
import { TrafficReportPopup } from '@/components/popup/TrafficReportPopup';
import { FeatureGuard } from '@/modules/FeatureGuard';
import { useMapStore } from '@/stores/useMapStore';
import { useUIStore } from '@/stores/useUIStore';
import { MapLayer } from '@/types/MapLayer';
import type { ParkingFeature } from '@/types/ParkingFeature';
import type { TrafficReportFeature } from '@/types/TrafficReportFeature';

const routeApi = getRouteApi('/_map');
// Typalias für den Search-Shape der '/_map'-Route, abgeleitet von useSearch
type MapSearch = ReturnType<typeof routeApi.useSearch>;

const TRAFFIC_REPORT_TEST_LAYER = 'TRAFFIC_REPORT_TEST';
const PARKING_TEST_LAYER = 'PARKING_TEST';

const SIDEBAR_NAV_WIDTH_EXPANDED = 192;
const SIDEBAR_NAV_WIDTH_COLLAPSED = 68;
const SIDEBAR_CONTENT_WIDTH_APPROX = 400;

const FIT_BOUNDS_MAX_ZOOM = 18;

const getFeatureGeometry = (properties: Record<string, unknown>) => {
  const geomString = properties.geom ?? properties.originalGeometry ?? properties.geom_original;

  if (typeof geomString !== 'string') return undefined;

  try {
    return JSON.parse(geomString) as Geometry;
  } catch {
    return undefined;
  }
};

export function RoutingMap() {
  const map = useRef<MapRef>(null);

  const viewState = useMapStore((state) => state.viewState);
  const selectedFeature = useMapStore((state) => state.selectedFeature);
  const { setViewState, setSelectedFeature } = useMapStore((state) => state.actions);

  const [contextMenu, setContextMenu] = useState<{ longitude: number; latitude: number }>();

  const [trafficReportFeature, setTrafficReportFeature] = useState<TrafficReportFeature>();
  const [parkingFeature, setParkingFeature] = useState<ParkingFeature>();
  const [featureSelectMenu, setFeatureSelectMenu] = useState<MapGeoJSONFeature[]>();

  const { trafficReports, levelOfService, parking } = useMapStore((state) => state.layersControl);

  const searchParams = routeApi.useSearch();
  const { popup, id } = searchParams;
  const navigate = routeApi.useNavigate();
  const sidebarExpanded = useUIStore((state) => state.sidebarExpanded);

  const fitBoundsPadding = useMemo(() => {
    const sidebarNavWidth = sidebarExpanded ? SIDEBAR_NAV_WIDTH_EXPANDED : SIDEBAR_NAV_WIDTH_COLLAPSED;
    const leftPadding = sidebarNavWidth + SIDEBAR_CONTENT_WIDTH_APPROX + 100;

    return {
      top: 64,
      right: 64,
      bottom: 320,
      left: leftPadding,
    };
  }, [sidebarExpanded]);

  const clearPopupParams = useCallback(() => {
    navigate({
      to: '.',
      search: { ...(searchParams as MapSearch), popup: undefined, id: undefined },
      replace: false,
    });
  }, [navigate, searchParams]);

  const openPopupForFeature = useCallback(
    (feature: MapGeoJSONFeature) => {
      if (FeatureGuard.isTrafficReportFeature(feature)) {
        setParkingFeature(undefined);
        setTrafficReportFeature(feature);

        const geometry = getFeatureGeometry(feature.properties ?? {});

        if (geometry) {
          map.current?.fitBounds(bbox(geometry) as maplibregl.LngLatBoundsLike, {
            padding: fitBoundsPadding,
            maxZoom: FIT_BOUNDS_MAX_ZOOM,
          });
        }
      } else if (FeatureGuard.isParkingFeature(feature)) {
        setTrafficReportFeature(undefined);
        setParkingFeature(feature);
      }
    },
    [fitBoundsPadding],
  );

  const handleFeatureSelect = useCallback(
    (feature: MapGeoJSONFeature) => {
      if (FeatureGuard.isTrafficReportFeature(feature)) {
        const reportId = feature.properties?.id;
        if (typeof reportId !== 'undefined') {
          navigate({
            to: '.',
            search: { ...(searchParams as MapSearch), popup: true, id: reportId },
            replace: false,
          });
        }

        openPopupForFeature(feature);
      } else if (FeatureGuard.isParkingFeature(feature)) {
        const facilityId = feature.properties?.parking_facility_id;
        if (facilityId) {
          navigate({
            to: '.',
            search: { ...(searchParams as MapSearch), popup: true, id: facilityId },
            replace: false,
          });
        }

        openPopupForFeature(feature);
      }
    },
    [navigate, openPopupForFeature, searchParams],
  );

  useEffect(() => {
    if (!popup || typeof id === 'undefined' || id === null) {
      setTrafficReportFeature(undefined);
      setParkingFeature(undefined);

      return;
    }

    const queryRenderedFeatures = (layers: string[]): MapGeoJSONFeature[] => {
      try {
        const mapInstance = map.current;
        if (!mapInstance) return [];

        return mapInstance.queryRenderedFeatures(undefined, { layers }) ?? [];
      } catch {
        return [];
      }
    };

    const idStr = String(id);
    const trafficLayers = [MapLayer.TRAFFIC_REPORT, TRAFFIC_REPORT_TEST_LAYER];
    const parkingLayers = [MapLayer.PARKING, PARKING_TEST_LAYER];

    const trafficFeatures = queryRenderedFeatures(trafficLayers);
    const foundTraffic = trafficFeatures.find((f) => {
      const featureId = f.id ?? f.properties?.id;

      return String(featureId) === idStr;
    });

    if (foundTraffic) {
      openPopupForFeature(foundTraffic);

      return;
    }

    const parkingFeatures = queryRenderedFeatures(parkingLayers);
    const foundParking = parkingFeatures.find((f) => String(f.properties?.parking_facility_id) === idStr);

    if (foundParking) {
      openPopupForFeature(foundParking);
    } else {
      setTrafficReportFeature(undefined);
      setParkingFeature(undefined);
    }
  }, [popup, id, openPopupForFeature]);

  return (
    <Map
      ref={map}
      {...viewState}
      attributionControl={false}
      dragRotate={false}
      mapStyle="https://sgx.geodatenzentrum.de/gdz_basemapde_vektor/styles/bm_web_gry.json"
      onMove={(event) => setViewState(event.viewState)}
      pitchWithRotate={false}
      touchPitch={false}
      interactiveLayerIds={[
        ...(parking ? [MapLayer.PARKING, PARKING_TEST_LAYER] : []),
        ...(trafficReports ? [MapLayer.TRAFFIC_REPORT, TRAFFIC_REPORT_TEST_LAYER] : []),
        ...(levelOfService ? [MapLayer.LEVEL_OF_SERVICE] : []),
      ]}
      onClick={(event) => {
        if (event.features && event.features.length > 0) {
          const { features } = event;

          const filteredFeatures = features.filter(
            (feature) => JSON.stringify(feature.geometry) === JSON.stringify(features[0].geometry),
          );

          if (filteredFeatures.length === 1) {
            handleFeatureSelect(filteredFeatures[0]);
          } else if (filteredFeatures.length > 1) {
            setFeatureSelectMenu(filteredFeatures);
          }
        } else {
          clearPopupParams();
          setTrafficReportFeature(undefined);
          setParkingFeature(undefined);
          setFeatureSelectMenu(undefined);
        }
      }}
      onContextMenu={(event) => {
        setContextMenu({ longitude: event.lngLat.lng, latitude: event.lngLat.lat });
      }}
    >
      {!trafficReportFeature && <AttributionControl position="bottom-right" />}

      <BorderShape />

      <LayerVisible layer="parking">
        <ParkingLayer />
      </LayerVisible>
      <LayerVisible layer="levelOfService">
        <LevelOfServiceLayer />
      </LayerVisible>
      <LayerVisible layer="trafficReports">
        <TrafficReportLayer />
      </LayerVisible>
      <LayerVisible layer="iaa2025">
        <JsonLayer layer="iaa2025" />
      </LayerVisible>
      <LayerVisible layer="marathon2025">
        <JsonLayer layer="marathon2025" />
      </LayerVisible>
      <LayerVisible layer="priorityNetworkFireDepartment">
        <JsonLayer layer="priorityNetworkFireDepartment" />
      </LayerVisible>
      <LayerVisible layer="tumAudimaxEvacuation">
        <JsonLayer layer="tumAudimaxEvacuation" />
      </LayerVisible>
      <LayerVisible layer="studyArea">
        <JsonLayer layer="studyArea" />
      </LayerVisible>

      {selectedFeature ? (
        <LayerPopup
          content={selectedFeature.content}
          coordinates={selectedFeature.coordinates}
          title={selectedFeature.title}
          onClose={() => {
            setSelectedFeature(undefined);
          }}
        />
      ) : null}

      <RouteLayers />
      <RouteLocationMarkers />

      <FeatureSelectMenuMarker
        features={featureSelectMenu}
        onClose={() => setFeatureSelectMenu(undefined)}
        onSelect={(feature) => handleFeatureSelect(feature)}
      />
      <ParkingPopup feature={parkingFeature} onClose={clearPopupParams} />
      <TrafficReportPopup feature={trafficReportFeature} onClose={clearPopupParams} />

      <ContextMenuMarker
        latitude={contextMenu?.latitude}
        longitude={contextMenu?.longitude}
        onClose={() => setContextMenu(undefined)}
      />
    </Map>
  );
}
